/**
 * Activation funnel analytics (#4910).
 *
 * Defines the canonical PostHog event names for the new-user activation funnel
 * and the small pure helpers used to emit them. Centralizing the event names
 * here keeps the funnel definition in one place and prevents string drift
 * across the emit sites (auth provider, research submit, stream completion).
 *
 * The funnel (measured in PostHog as a unique-user ordered funnel):
 *   signup_completed → research_query_submitted → research_answer_received
 *
 * `signup_completed` is the funnel entry; `research_answer_received` is the
 * activation event — the "aha moment" where a new user first receives a cited
 * legal answer. Per SaaS activation research the goal is to maximize the share
 * of signups that reach this step.
 *
 * Cohort semantics (post-#4940): PostHog is opt-out-by-default, so
 * `signup_completed` fires only for users who granted analytics consent. It is
 * therefore a *consenting-cohort* funnel entry — a conversion-rate instrument,
 * not an absolute signup counter. Absolute signup truth is read from Supabase
 * `auth.users` (the source of truth), never reconciled 1:1 against PostHog.
 * This split is deliberate: GDPR applies regardless of where an event is
 * emitted, so a server-side "delay-proof" emit for non-consenting users was
 * rejected — it would re-introduce the pre-consent transmission #4940 removed.
 * See #4941.
 *
 * Emit sites are deliberately thin (one `capture()` call each) so this module
 * carries the only logic worth unit-testing.
 */

export const ACTIVATION_EVENT = {
  /** Fired once when a brand-new account is created (funnel entry). */
  signupCompleted: 'signup_completed',
  /** Fired each time a returning user explicitly signs in (not a new signup). */
  userLoggedIn: 'user_logged_in',
  /** Fired each time a user submits a research/chat query. */
  querySubmitted: 'research_query_submitted',
  /** Fired when the LLM answer stream completes — the activation "aha". */
  answerReceived: 'research_answer_received',
  /** Fired server-side when a Stripe subscription is first created. */
  subscriptionStarted: 'subscription_started',
  /** Fired server-side when a Stripe subscription is cancelled/deleted. */
  subscriptionCancelled: 'subscription_cancelled',
  /** Fired server-side on invoice.paid (renewal or new subscription). */
  paymentSucceeded: 'payment_succeeded',
  /** Fired server-side on invoice.payment_failed. */
  paymentFailed: 'payment_failed',
  /** Fired server-side when a one-time credit pack is purchased. */
  packPurchased: 'pack_purchased',
} as const;

/**
 * How recently an auth user must have been created to count their sign-in as a
 * freshly-completed signup.
 *
 * Supabase's `onAuthStateChange` emits `SIGNED_IN` for both login and signup —
 * there is no dedicated `SIGNED_UP` event. We treat a sign-in as a signup when
 * the account was created within this window, deduplicated to once-per-user by
 * the `signupEmittedKey` localStorage guard. The window's only job is to bound
 * how *late* a genuine first sign-in can still be recognised as a signup; the
 * guard — not the window — is what stops a returning user's normal login being
 * miscounted (only an account whose own `created_at` is inside the window can
 * fire, and such an account is by definition a new user).
 *
 * Sized at 24h, not minutes (#4941): email-confirmation delivery on some
 * corporate / on-prem SK mail servers lags well past a few minutes, so the
 * original 5-minute window dropped those signups from the funnel entirely (the
 * sign-in landed outside the window and the once-per-user guard then made the
 * miss permanent). A full day comfortably covers the signup → email-confirm →
 * first-redirect tail; widening is safe precisely because the guard, not the
 * window, prevents double- or mis-counting (see above).
 */
export const SIGNUP_RECENCY_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Whether an auth user's `created_at` is recent enough to treat this sign-in as
 * a freshly-completed signup. Returns false for missing/unparseable timestamps
 * and for `created_at` values in the future (clock skew) so we never emit a
 * spurious signup event.
 */
export function isRecentSignup(
  createdAt: string | null | undefined,
  nowMs: number
): boolean {
  if (!createdAt) return false;
  const createdMs = Date.parse(createdAt);
  if (Number.isNaN(createdMs)) return false;
  const ageMs = nowMs - createdMs;
  return ageMs >= 0 && ageMs <= SIGNUP_RECENCY_WINDOW_MS;
}

/**
 * localStorage key guarding `signup_completed` against double-emission. The
 * recency window (above) spans many page loads / auth events, so without a
 * per-user guard a fresh user signing in repeatedly inside the window would
 * fire the event more than once. PostHog funnels count unique users, but a
 * clean one-per-user signal keeps raw event counts trustworthy.
 */
export function signupEmittedKey(userId: string): string {
  return `ph_signup_emitted:${userId}`;
}

/**
 * Window event dispatched by the PostHog provider (phostHogproviders.tsx) the
 * moment analytics capturing flips to opted-in. SupabaseAuthProvider listens
 * for it and re-runs `syncPostHogUser` against the live session.
 *
 * Without this, consent and the auth-keyed emit live in separate providers that
 * only meet on a Supabase auth event: a user who signs up, declines, then
 * consents *mid-session with no subsequent sign-in / reload / token refresh*
 * would not be identified — and a `signup_completed` suppressed pre-consent
 * would not fire — until their next auth event (#4966). Emitting on opt-in
 * closes that gap immediately. The handler is idempotent (PostHog `identify`
 * is idempotent; `signup_completed` stays once-per-user via signupEmittedKey),
 * so a redundant fire on a returning-consented load is harmless.
 */
export const POSTHOG_OPTED_IN_EVENT = 'posthog:opted-in';
